/**
 * Canonical client-side domain normaliser — ONE definition, portal-wide.
 *
 * Extracted from `obw/steps/StepDomainVerify.tsx` on 13 August 2026 when the
 * public sign-up modal gained its own "Website domain" field (ADR-105 D1
 * Amendment 2). Two fields now capture the same value at two moments in one
 * journey — the modal captures it, `domain-verify` confirms it — and the ONLY
 * way they cannot disagree is by sharing this function rather than each holding
 * a copy of it. A second copy would be a constant that drifts: the modal would
 * accept a form the step rejected, and the pre-fill would arrive already broken.
 *
 * Mirrors the server-side `normaliseDomain` (`lambdas/account/src/bow.ts`) so
 * client and server agree on the canonical form that reaches the provision
 * route. Accepts bare (`hvt.ai`), `www.`-prefixed, and full-URL inputs.
 */

/**
 * Normalise a user-entered domain to a canonical `{ url, hostname }`, or `null`
 * if it is not a plausible domain.
 */
export function normaliseDomainInput(input: string): { url: string; hostname: string } | null {
  let raw = input.trim().toLowerCase();
  if (!raw) return null;
  /**
   * ⭐⭐ REJECT `@` — AN EMAIL ADDRESS MUST NOT NORMALISE INTO A DOMAIN.
   * (Found 13 Aug 2026 by the Stage 3 test; PRE-EXISTING in this normaliser, so
   * it affected `domain-verify` on EVERY journey, not just the new sign-up
   * field.)
   *
   * `new URL('https://owner@holdingco.com').hostname` is `holdingco.com` — the
   * parser treats `owner@` as USERINFO and silently drops it. A visitor pasting
   * their email into a domain field therefore got the email's domain, marked
   * "✓ Looks good", and would have had that domain MINTED as the site to
   * monitor. `owner@holdingco.com` monitoring `retailbrand.com` is the ordinary
   * case ADR-105 D1 names, which makes this a live wrong-site risk rather than a
   * theoretical one.
   */
  if (raw.includes('@')) return null;
  if (!/^https?:\/\//i.test(raw)) raw = `https://${raw}`;
  let host: string;
  try {
    host = new URL(raw).hostname;
  } catch {
    return null;
  }
  const bare = host.replace(/^www\./, '');
  // Require at least one dot and valid DNS labels (letters/digits/hyphens, no
  // leading/trailing hyphen) — rejects "localhost", trailing-dot, bad chars.
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(bare)) {
    return null;
  }
  return { url: `https://${bare}`, hostname: bare };
}
